/**
 * Blog Listing Page Logic
 *
 * Handles client-side pagination, filtering, and search for blog posts
 */ /**
 * Date Formatter
 *
 * Utility class for formatting dates in English locale ("en-US")
 * Uses native Intl.DateTimeFormat for zero-dependency implementation
 */ class $8543ab00c05405d0$export$ad991b66133851cf {
    /**
   * Format date as full English date: "February 16, 2026"
   */ static fullDate(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric"
        }).format(d);
    }
    /**
   * Format date as short English date: "02/16/2026"
   */ static shortDate(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        }).format(d);
    }
    /**
   * Format date as ISO 8601: "2026-02-16"
   */ static isoDate(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        const [datePart] = d.toISOString().split("T");
        return datePart ?? "";
    }
    /**
   * Format relative time in English: "2 days ago", "3 weeks ago"
   */ static relativeTime(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays === 0) return "today";
        else if (diffDays === 1) return "yesterday";
        else if (diffDays < 7) return `${diffDays} days ago`;
        else if (diffDays < 30) {
            const weeks = Math.floor(diffDays / 7);
            return weeks === 1 ? "1 week ago" : `${weeks} weeks ago`;
        } else if (diffDays < 365) {
            const months = Math.floor(diffDays / 30);
            return months === 1 ? "1 month ago" : `${months} months ago`;
        } else {
            const years = Math.floor(diffDays / 365);
            return years === 1 ? "1 year ago" : `${years} years ago`;
        }
    }
    /**
   * Format date with time: "February 16, 2026 2:30 PM"
   */ static fullDateTime(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
        }).format(d);
    }
    /**
   * Format month and year: "February 2026"
   */ static monthYear(date) {
        const d = typeof date === "string" ? new Date(date) : date;
        return new Intl.DateTimeFormat("en-US", {
            year: "numeric",
            month: "long"
        }).format(d);
    }
}


// State
let $cbbfece4737a5c0e$var$allPosts = [];
let $cbbfece4737a5c0e$var$filteredPosts = [];
let $cbbfece4737a5c0e$var$currentPage = 1;
const $cbbfece4737a5c0e$var$POSTS_PER_PAGE = 10;
// DOM elements
let $cbbfece4737a5c0e$var$postsContainer = null;
let $cbbfece4737a5c0e$var$noResultsContainer = null;
let $cbbfece4737a5c0e$var$paginationPrev = null;
let $cbbfece4737a5c0e$var$paginationNext = null;
let $cbbfece4737a5c0e$var$paginationInfo = null;
let $cbbfece4737a5c0e$var$filterSearch = null;
let $cbbfece4737a5c0e$var$filterCategory = null;
let $cbbfece4737a5c0e$var$clearFiltersBtn = null;
/**
 * Initialize blog listing
 */ async function $cbbfece4737a5c0e$var$init() {
    // Get DOM elements
    $cbbfece4737a5c0e$var$postsContainer = document.getElementById("blog-posts");
    $cbbfece4737a5c0e$var$noResultsContainer = document.getElementById("no-results");
    $cbbfece4737a5c0e$var$paginationPrev = document.getElementById("pagination-prev");
    $cbbfece4737a5c0e$var$paginationNext = document.getElementById("pagination-next");
    $cbbfece4737a5c0e$var$paginationInfo = document.getElementById("pagination-info");
    $cbbfece4737a5c0e$var$filterSearch = document.getElementById("filter-search");
    $cbbfece4737a5c0e$var$filterCategory = document.getElementById("filter-category");
    $cbbfece4737a5c0e$var$clearFiltersBtn = document.getElementById("clear-filters-btn");
    if (!$cbbfece4737a5c0e$var$postsContainer) {
        console.error("Blog posts container not found");
        return;
    }
    // Load blog index
    try {
        const response = await fetch("./blog/blog-index.json");
        if (!response.ok) throw new Error(`Failed to load blog index: ${response.statusText}`);
        const data = await response.json();
        $cbbfece4737a5c0e$var$allPosts = data.posts;
        $cbbfece4737a5c0e$var$filteredPosts = [
            ...$cbbfece4737a5c0e$var$allPosts
        ];
        // Populate category filter
        $cbbfece4737a5c0e$var$populateCategoryFilter(data.categories);
        // Set up event listeners
        $cbbfece4737a5c0e$var$setupEventListeners();
        // Render initial page
        $cbbfece4737a5c0e$var$renderPosts();
    } catch (error) {
        console.error("Error loading blog posts:", error);
        $cbbfece4737a5c0e$var$postsContainer.innerHTML = '<p class="error">Could not load blog posts. Please try again later.</p>';
    }
}
/**
 * Populate category filter dropdown
 */ function $cbbfece4737a5c0e$var$populateCategoryFilter(categories) {
    if (!$cbbfece4737a5c0e$var$filterCategory) return;
    // Keep "All categories" option
    const allOption = $cbbfece4737a5c0e$var$filterCategory.querySelector('option[value=""]');
    // Clear other options
    $cbbfece4737a5c0e$var$filterCategory.innerHTML = "";
    if (allOption) $cbbfece4737a5c0e$var$filterCategory.appendChild(allOption);
    // Add category options
    const categoryMap = {
        "open-source": "Open Source",
        "consulting": "Consulting",
        "ai": "AI",
        "product": "Product",
        "case-study": "Case Study"
    };
    for (const [category, count] of Object.entries(categories)){
        const option = document.createElement("option");
        option.value = category;
        option.textContent = `${categoryMap[category] || category} (${count})`;
        $cbbfece4737a5c0e$var$filterCategory.appendChild(option);
    }
}
/**
 * Set up event listeners
 */ function $cbbfece4737a5c0e$var$setupEventListeners() {
    if ($cbbfece4737a5c0e$var$paginationPrev) $cbbfece4737a5c0e$var$paginationPrev.addEventListener("click", ()=>{
        if ($cbbfece4737a5c0e$var$currentPage > 1) {
            $cbbfece4737a5c0e$var$currentPage--;
            $cbbfece4737a5c0e$var$renderPosts();
        }
    });
    if ($cbbfece4737a5c0e$var$paginationNext) $cbbfece4737a5c0e$var$paginationNext.addEventListener("click", ()=>{
        const totalPages = Math.ceil($cbbfece4737a5c0e$var$filteredPosts.length / $cbbfece4737a5c0e$var$POSTS_PER_PAGE);
        if ($cbbfece4737a5c0e$var$currentPage < totalPages) {
            $cbbfece4737a5c0e$var$currentPage++;
            $cbbfece4737a5c0e$var$renderPosts();
        }
    });
    if ($cbbfece4737a5c0e$var$filterSearch) $cbbfece4737a5c0e$var$filterSearch.addEventListener("input", ()=>{
        $cbbfece4737a5c0e$var$applyFilters();
    });
    if ($cbbfece4737a5c0e$var$filterCategory) $cbbfece4737a5c0e$var$filterCategory.addEventListener("change", ()=>{
        $cbbfece4737a5c0e$var$applyFilters();
    });
    if ($cbbfece4737a5c0e$var$clearFiltersBtn) $cbbfece4737a5c0e$var$clearFiltersBtn.addEventListener("click", ()=>{
        $cbbfece4737a5c0e$var$clearFilters();
    });
}
/**
 * Clear all filters and reset view
 */ function $cbbfece4737a5c0e$var$clearFilters() {
    if ($cbbfece4737a5c0e$var$filterSearch) $cbbfece4737a5c0e$var$filterSearch.value = "";
    if ($cbbfece4737a5c0e$var$filterCategory) $cbbfece4737a5c0e$var$filterCategory.value = "";
    $cbbfece4737a5c0e$var$applyFilters();
}
/**
 * Apply filters and reset to page 1
 */ function $cbbfece4737a5c0e$var$applyFilters() {
    const searchTerm = $cbbfece4737a5c0e$var$filterSearch?.value.toLowerCase() || "";
    const selectedCategory = $cbbfece4737a5c0e$var$filterCategory?.value || "";
    $cbbfece4737a5c0e$var$filteredPosts = $cbbfece4737a5c0e$var$allPosts.filter((post)=>{
        // Search filter (title, excerpt, tags, author)
        const matchesSearch = !searchTerm || post.title.toLowerCase().includes(searchTerm) || post.excerpt.toLowerCase().includes(searchTerm) || post.author.toLowerCase().includes(searchTerm) || post.tags.some((tag)=>tag.toLowerCase().includes(searchTerm));
        // Category filter
        const matchesCategory = !selectedCategory || post.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });
    $cbbfece4737a5c0e$var$currentPage = 1;
    $cbbfece4737a5c0e$var$renderPosts();
}
/**
 * Render posts for current page
 */ function $cbbfece4737a5c0e$var$renderPosts() {
    if (!$cbbfece4737a5c0e$var$postsContainer || !$cbbfece4737a5c0e$var$noResultsContainer) return;
    // Calculate pagination
    const totalPages = Math.ceil($cbbfece4737a5c0e$var$filteredPosts.length / $cbbfece4737a5c0e$var$POSTS_PER_PAGE);
    const startIdx = ($cbbfece4737a5c0e$var$currentPage - 1) * $cbbfece4737a5c0e$var$POSTS_PER_PAGE;
    const endIdx = startIdx + $cbbfece4737a5c0e$var$POSTS_PER_PAGE;
    const postsToShow = $cbbfece4737a5c0e$var$filteredPosts.slice(startIdx, endIdx);
    // Show/hide empty state
    if ($cbbfece4737a5c0e$var$filteredPosts.length === 0) {
        $cbbfece4737a5c0e$var$postsContainer.style.display = "none";
        $cbbfece4737a5c0e$var$noResultsContainer.style.display = "block";
    } else {
        $cbbfece4737a5c0e$var$postsContainer.style.display = "grid";
        $cbbfece4737a5c0e$var$noResultsContainer.style.display = "none";
        $cbbfece4737a5c0e$var$postsContainer.innerHTML = postsToShow.map((post)=>$cbbfece4737a5c0e$var$renderPostCard(post)).join("");
    }
    // Update pagination controls
    $cbbfece4737a5c0e$var$updatePagination(totalPages);
    // Scroll to top
    window.scrollTo({
        top: 0,
        behavior: "smooth"
    });
}
/**
 * Render a single post card
 */ function $cbbfece4737a5c0e$var$renderPostCard(post) {
    const formattedDate = (0, $8543ab00c05405d0$export$ad991b66133851cf).fullDate(post.date);
    const categoryLabel = $cbbfece4737a5c0e$var$getCategoryLabel(post.category);
    // Truncate excerpt if too long
    const truncatedExcerpt = post.excerpt.length > 150 ? post.excerpt.substring(0, 150) + "..." : post.excerpt;
    return `
    <article class="post-card">
      <div class="post-card-meta">
        <time datetime="${post.date}">${formattedDate}</time>
        <span class="post-card-category">${categoryLabel}</span>
      </div>
      <h2><a href="./blog/${post.slug}.html">${post.title}</a></h2>
      <p class="post-card-excerpt">${truncatedExcerpt}</p>
      <div class="post-card-tags">
        ${post.tags.map((tag)=>`<span class="tag">#${tag}</span>`).join("")}
      </div>
      <a href="./blog/${post.slug}.html" class="post-card-link">Read more</a>
    </article>
  `;
}
/**
 * Get category label
 */ function $cbbfece4737a5c0e$var$getCategoryLabel(category) {
    const labels = {
        "open-source": "Open Source",
        consulting: "Consulting",
        ai: "AI",
        product: "Product",
        "case-study": "Case Study"
    };
    return labels[category] || category;
}
/**
 * Update pagination controls
 */ function $cbbfece4737a5c0e$var$updatePagination(totalPages) {
    if ($cbbfece4737a5c0e$var$paginationPrev) $cbbfece4737a5c0e$var$paginationPrev.disabled = $cbbfece4737a5c0e$var$currentPage === 1;
    if ($cbbfece4737a5c0e$var$paginationNext) $cbbfece4737a5c0e$var$paginationNext.disabled = $cbbfece4737a5c0e$var$currentPage === totalPages || totalPages === 0;
    if ($cbbfece4737a5c0e$var$paginationInfo) {
        if (totalPages === 0) $cbbfece4737a5c0e$var$paginationInfo.textContent = "No posts";
        else $cbbfece4737a5c0e$var$paginationInfo.textContent = `Page ${$cbbfece4737a5c0e$var$currentPage} of ${totalPages}`;
    }
}
/**
 * Setup sticky header scroll handler
 */ function $cbbfece4737a5c0e$var$setupStickyHeader() {
    const header = document.querySelector('header');
    if (!header) return;
    // Add blog-header class for sticky behavior
    header.classList.add('blog-header');
    window.addEventListener('scroll', ()=>{
        if (window.scrollY > 50) header.classList.add('scrolled');
        else header.classList.remove('scrolled');
    });
}
/**
 * Setup back to top button
 */ function $cbbfece4737a5c0e$var$setupBackToTop() {
    // Create back to top button if it doesn't exist
    let backToTop = document.getElementById('back-to-top');
    if (!backToTop) {
        backToTop = document.createElement('button');
        backToTop.id = 'back-to-top';
        backToTop.className = 'back-to-top';
        backToTop.setAttribute('aria-label', 'Back to top');
        backToTop.innerHTML = "\u2191";
        document.body.appendChild(backToTop);
    }
    // Show/hide button based on scroll position
    window.addEventListener('scroll', ()=>{
        if (window.scrollY > 300) backToTop.classList.add('visible');
        else backToTop.classList.remove('visible');
    });
    // Scroll to top on click
    backToTop.addEventListener('click', ()=>{
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    });
}
// Initialize on DOM load
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", ()=>{
    $cbbfece4737a5c0e$var$init();
    $cbbfece4737a5c0e$var$setupStickyHeader();
    $cbbfece4737a5c0e$var$setupBackToTop();
});
else {
    $cbbfece4737a5c0e$var$init();
    $cbbfece4737a5c0e$var$setupStickyHeader();
    $cbbfece4737a5c0e$var$setupBackToTop();
}


//# sourceMappingURL=blog.127068c0.js.map
